import { inject, Injectable, signal } from '@angular/core';
import { catchError, EMPTY, finalize, Observable, switchMap, tap } from 'rxjs';
import { DmsIntegrationApiService, downloadFileFromUrl, Logger, SnackbarService, SnackbarType, TrackService } from '@aiwyn/shared';
import { FilePreviewService } from '@aiwyn/services';
import { CxpDeliverableFile } from '../models';
import { CxpDeliverableApiService, MarkDeliverableFilesActions } from './cxp-deliverable-api.service';

/**
 * Stateless action service for the deliverable surface area:
 *   - Per-file preview / download (CXP detail page) + tracking the action
 *     against the backend (`MarkDeliverableFiles` mutation).
 *   - "Mark all + open external" — used when the user clicks View on an
 *     external-deliverable (e.g. SafeSend) row in the tasks list. We mark
 *     every file as VIEWED, await the response, then redirect.
 */
@Injectable({ providedIn: 'root' })
export class CxpDeliverableActionsService {
  private readonly dmsIntegrationApi = inject(DmsIntegrationApiService);
  private readonly filePreviewService = inject(FilePreviewService);
  private readonly deliverableApi = inject(CxpDeliverableApiService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly logger = inject(Logger);
  private readonly trackService = inject(TrackService);

  private readonly _isDownloadingAll = signal(false);
  private readonly _fileIdsDownloading = signal<Record<number, boolean>>({});
  private readonly _isOpeningExternal = signal(false);

  readonly isDownloadingAll = this._isDownloadingAll.asReadonly();
  readonly isOpeningExternal = this._isOpeningExternal.asReadonly();

  /**
   * Type-safe lookup for per-file download progress. The underlying map is
   * intentionally kept private — consumers should not index into it directly.
   * Add a new helper method here if a different read shape is needed.
   */
  isFileDownloading(fileId: number): boolean {
    return this._fileIdsDownloading()[fileId] === true;
  }

  previewFile(deliverableId: number, file: CxpDeliverableFile): void {
    if (file.id === undefined || file.id === null) {
      this.logger.error('[CxpDeliverableActionsService] previewFile called with no file id');
      return;
    }

    this.trackService.track('CXP Deliverable Preview File', { deliverableId, fileId: file.id });

    // Fire-and-forget mark VIEWED — matches the legacy behavior in
    // `client-requests/components/deliverable/deliverable.component.ts`.
    this.markFiles(deliverableId, [file.id], MarkDeliverableFilesActions.VIEWED);

    this.filePreviewService.preview({
      id: file.id,
      name: file.name,
      dmsFileId: file.dmsId ?? undefined,
    });
  }

  downloadAll(deliverableId: number, files: CxpDeliverableFile[]): void {
    if (this._isDownloadingAll() || files.length === 0) return;

    this._isDownloadingAll.set(true);
    this.trackService.track('CXP Deliverable Download All', {
      deliverableId,
      fileCount: files.length,
    });

    this.markFiles(
      deliverableId,
      files.map((f) => f.id),
      MarkDeliverableFilesActions.DOWNLOADED,
    );

    this.runDownload(
      files.map((f) => f.id),
      files.map((f) => f.name).join(', '),
      true,
    )
      .pipe(finalize(() => this._isDownloadingAll.set(false)))
      .subscribe();
  }

  downloadOne(deliverableId: number, file: CxpDeliverableFile): void {
    if (this._fileIdsDownloading()[file.id]) return;

    this.markFileDownloading(file.id, true);
    this.trackService.track('CXP Deliverable Download File', { deliverableId, fileId: file.id });

    this.markFiles(deliverableId, [file.id], MarkDeliverableFilesActions.DOWNLOADED);

    this.runDownload([file.id], file.name, false)
      .pipe(finalize(() => this.markFileDownloading(file.id, false)))
      .subscribe();
  }

  /**
   * For external-deliverable rows: mark every file on the deliverable as
   * VIEWED, wait for the backend to acknowledge, then navigate to the
   * third-party URL in the same window. The mark is awaited (not
   * fire-and-forget) so the user lands on the external surface with the
   * deliverable's tracking already updated server-side.
   *
   * If the file fetch or mark call fails, we still redirect — the user
   * shouldn't be stuck because of a tracking-side hiccup.
   */
  markAllAndOpenExternal(deliverableId: number, url: string): void {
    if (this._isOpeningExternal()) return;

    this._isOpeningExternal.set(true);
    this.trackService.track('CXP External Deliverable Opened', { deliverableId, url });

    this.deliverableApi
      .getDeliverableById(deliverableId)
      .pipe(
        switchMap((apiResponse) => {
          const fileIds = apiResponse?.fileIds.map((entry) => entry.fileId) ?? [];
          if (fileIds.length === 0) {
            // Nothing to mark — proceed to redirect.
            return EMPTY;
          }
          return this.deliverableApi.markDeliverableFiles(deliverableId, fileIds, MarkDeliverableFilesActions.VIEWED);
        }),
        catchError((err) => {
          this.logger.error('[CxpDeliverableActionsService] markAllAndOpenExternal failed:', err);
          return EMPTY;
        }),
        finalize(() => {
          this._isOpeningExternal.set(false);
          window.open(url, '_blank');
        }),
      )
      .subscribe();
  }

  private markFiles(deliverableId: number, fileIds: number[], action: MarkDeliverableFilesActions): void {
    if (fileIds.length === 0) return;
    this.deliverableApi.markDeliverableFiles(deliverableId, fileIds, action).subscribe({
      error: (err) => this.logger.error(`[CxpDeliverableActionsService] markDeliverableFiles(${action}) failed:`, err),
    });
  }

  private runDownload(fileIds: number[], downloadName: string, isMultiple: boolean): Observable<unknown> {
    return this.dmsIntegrationApi.getDownloadUrl({ files: fileIds, isMultipleDownload: isMultiple }).pipe(
      tap((url) => downloadFileFromUrl({ href: url, download: downloadName })),
      catchError((err) => {
        this.logger.error('[CxpDeliverableActionsService] getDownloadUrl failed:', err);
        this.snackbarService.open('Download failed. Please try again.', SnackbarType.ERROR);
        return EMPTY;
      }),
    );
  }

  private markFileDownloading(fileId: number, isDownloading: boolean): void {
    this._fileIdsDownloading.set({
      ...this._fileIdsDownloading(),
      [fileId]: isDownloading,
    });
  }
}
